import type { ReactNode } from 'react';
import { useI18n } from '../../i18n';
import { countLabel } from '../../i18n/labels';
import { ownTeamRounds, panelCompared } from '../../utils/officialStats';
import type { OwnTeamCells, PanelCells } from '../../types';

// #641 — the tables of the panel cells and of the own-team cells. The page
// of an official shows them; #647 — the report of the officials of a
// tournament shows them for all the judges of the tournament. #661, F84 —
// the file also holds the two shared parts of the officials screens: the
// title of a section (`SectionTitle`) and the shell of a number (`StatTile`).

/** The shell of a tile: the one string of the border and the padding. */
export const STAT_TILE_CLS = 'rounded-lg border border-gray-100 p-3';

/** The title of a section of the officials screens, with its hint under it. */
export function SectionTitle({ title, hint }: { title: string; hint: ReactNode }) {
  return (
    <div>
      <div className="font-semibold text-sm">{title}</div>
      <p className="text-xs text-gray-500">{hint}</p>
    </div>
  );
}

/** A tile with a number: the title at the left of the header row, the value at
 *  the right, an optional detail line and an optional help line. The body of a
 *  tile (a table, a trend) is `children`. `strongTitle` gives the title the
 *  dark semibold style (a table, the neutrality tile). `valueSmall` gives the
 *  value the small style of a card count. The test id and the data attributes
 *  stay on the shell. */
export function StatTile({ testId, attrs, title, value, valueSmall = false, detail, help, strongTitle = false, className, children }: {
  testId: string; attrs?: Record<string, string | number>; title: ReactNode; value?: ReactNode; valueSmall?: boolean;
  detail?: ReactNode; help?: string; strongTitle?: boolean; className?: string; children?: ReactNode;
}) {
  return (
    <div data-testid={testId} {...attrs} className={className ? `${STAT_TILE_CLS} ${className}` : STAT_TILE_CLS}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={strongTitle ? 'text-xs font-semibold text-gray-700' : 'text-xs text-gray-500'}>{title}</span>
        {value !== undefined && (
          <span className={valueSmall ? 'text-xs text-gray-500 tabular-nums' : 'text-lg font-black tabular-nums'}>{value}</span>
        )}
      </div>
      {detail && <div className="text-xs text-gray-500 text-right tabular-nums">{detail}</div>}
      {help && <p className="text-xs text-gray-400 mt-1">{help}</p>}
      {children}
    </div>
  );
}

/** #641 — the four cells of the panel and the jury, as a 2×2 table: the rows
 *  are the panel, the columns are the jury. The cell of an upheld dissent has
 *  the colour of a good mark. */
export function PanelTable({ testId, title, cells }: { testId: string; title: string; cells: PanelCells }) {
  const { t, lang } = useI18n();
  const total = panelCompared(cells);
  const cell = (key: keyof PanelCells, good = false) => (
    <div data-cell={key} data-count={cells[key]}
      className={`rounded-md px-2 py-1.5 text-center text-lg font-black tabular-nums ${good ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-800'}`}>
      {cells[key]}
    </div>
  );
  return (
    <StatTile testId={testId} title={title} strongTitle valueSmall className="space-y-2"
      value={countLabel(lang, total, { one: t.officials.panelCardsOne, few: t.officials.panelCardsFew, other: t.officials.panelCardsOther })}>
      <div className="grid grid-cols-3 gap-1.5 items-center text-xs">
        <span />
        <span className="text-center text-gray-500">{t.officials.withJury}</span>
        <span className="text-center text-gray-500">{t.officials.againstJury}</span>
        <span className="text-gray-500 break-words">{t.officials.withPanel}</span>
        {cell('consensus')}
        {cell('panel_overruled')}
        <span className="text-gray-500 break-words">{t.officials.againstPanel}</span>
        {cell('dissent_upheld', true)}
        {cell('dissent_overruled')}
      </div>
    </StatTile>
  );
}

/** #641 — the own-team cards against the jury decision (the approved round,
 *  the winner of a WP bout): for the own corner, with the jury, against the
 *  own corner (operator, 2026-10-22). */
export function OwnTeamTable({ testId, title, cells }: { testId: string; title: string; cells: OwnTeamCells }) {
  const { t, lang } = useI18n();
  const total = ownTeamRounds(cells);
  const row = (key: keyof OwnTeamCells, label: string, warn = false) => (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-gray-500">{label}</span>
      <span data-cell={key} data-count={cells[key]}
        className={`rounded-md px-2 py-0.5 text-sm font-black tabular-nums ${warn && cells[key] > 0 ? 'bg-amber-50 text-amber-800' : 'bg-gray-50 text-gray-800'}`}>
        {cells[key]}
      </span>
    </div>
  );
  return (
    <StatTile testId={testId} title={title} strongTitle valueSmall className="space-y-1.5"
      value={countLabel(lang, total, { one: t.officials.panelCardsOne, few: t.officials.panelCardsFew, other: t.officials.panelCardsOther })}>
      {row('for_own', t.officials.forOwn, true)}
      {row('with_jury', t.officials.withJury)}
      {row('against_own', t.officials.againstOwn, true)}
    </StatTile>
  );
}
